// Copied from ProfilePage.tsx (renamed to Profile.tsx)
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { updateProfile } from "@services/auth";
import { resolveWorkingOrganizationName } from "@utils/workingOrganizationName";
import ChangePasswordContent from "./ChangePasswordContent";

/** Same title band as Regulations Library (Discovery 80px + 20px). */
const PROFILE_TITLE_BAND_HEIGHT_PX = 100;

const PageContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "0px 64px 24px",
  height: `${PROFILE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PROFILE_TITLE_BAND_HEIGHT_PX}px`,
  display: "flex",
  alignItems: "flex-end",
  flexShrink: 0,
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "0px 16px 16px",
    height: "auto",
    minHeight: `${PROFILE_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: "1 0 auto",
  padding: "16px 48px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflow: "visible",
  [theme.breakpoints.down("md")]: {
    padding: "2px 24px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "2px 12px 24px",
  },
}));

const TabsContainer = styled(Box)({
  display: "flex",
  gap: "0px",
  alignItems: "center",
  //marginBottom: "16px",
});

const Tab = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(({ isActive }) => ({
  padding: "8px 12px",
  cursor: "pointer",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "15px",
  fontWeight: isActive ? 500 : 400,
  color: isActive ? "#065b70" : "#737373",
  borderBottom: isActive ? "1px solid #065b70" : "none",
  transition: "all 0.2s",
  "&:hover": {
    color: "#065b70",
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  alignSelf: "flex-start",
  maxWidth: 744, // two fields (360px each) + gap (24px)
  width: "100%",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
  },
}));

const ButtonContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  justifyContent: "flex-end",
  marginBottom: "16px",
});

const EditButton = styled(Button)({
  background: "#e6fafc",
  border: "1px solid #5bbfd6",
  borderRadius: "8px",
  padding: "8px 16px",
  minHeight: "36px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  color: "#086e86",
  "&:hover": {
    background: "#d8f4f8",
  },
});

const SaveButton = styled(Button)({
  background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
  borderRadius: "8px",
  padding: "8px 16px",
  minHeight: "36px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  color: "#e6fafc",
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.25)",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #4ab3c7 100%)",
  },
});

const CancelButton = styled(Button)({
  background: "white",
  border: "1px solid #b3b3b3",
  borderRadius: "8px",
  padding: "8px 16px",
  minHeight: "36px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  color: "#262626",
  "&:hover": {
    background: "#f5f5f5",
  },
});

const FieldsRow = styled(Box)({
  display: "flex",
  gap: "36px",
  width: "100%",
});

const FieldContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: 1,
  maxWidth: 360, // reduce field width on desktop
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
  },
}));

const FieldLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  color: "#065b70",
  padding: "4px",
});

const FieldValue = styled(Box)({
  background: "#f9f9f9",
  border: "1px solid #e5e5e5",
  borderRadius: "8px",
  padding: "8px",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  color: "#262626",
  margin: "4px",
  width: "100%",
});

const StyledTextField = styled(TextField)({
  margin: "4px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fdffff",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#99d4e1",
    },
    "&:hover fieldset": {
      borderColor: "#5bbfd6",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
    color: "#262626",
  },
});

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  organizationName: string;
}

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"personal" | "password">("personal");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    organizationName: "",
  });
  const [editedData, setEditedData] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    organizationName: "",
  });

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const data: ProfileData = {
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          phoneNumber: user.phone_number || user.phone || "",
          organizationName: resolveWorkingOrganizationName(),
        };
        setProfileData(data);
        setEditedData(data);
      } catch (error) {
        console.error("Failed to parse user data:", error);
      }
    } else {
      const organizationName = resolveWorkingOrganizationName();
      if (organizationName) {
        setProfileData((prev) => ({ ...prev, organizationName }));
        setEditedData((prev) => ({ ...prev, organizationName }));
      }
    }
  }, []);

  const handleEdit = () => setIsEditMode(true);
  const handleCancel = () => {
    setEditedData(profileData);
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Call the API to update profile
      // Now also sending phone_number when available
      const payload = {
        first_name: editedData.firstName.trim(),
        last_name: editedData.lastName.trim(),
        display_name: editedData.firstName && editedData.lastName 
          ? `${editedData.firstName} ${editedData.lastName}`.trim()
          : undefined,
        phone_number: editedData.phoneNumber.trim() || undefined,
      };

      const response = await updateProfile(payload);
      console.log("Profile update response:", response); // Debug logging

      // If API call succeeded (no exception thrown), treat as success
      // Handle different response formats
      const responseData = response.data || (response as any);
      
      if (response.success !== false) {
        // Update local state with the response data from server
        const updatedProfileData: ProfileData = {
          firstName: responseData.first_name || editedData.firstName,
          lastName: responseData.last_name || editedData.lastName,
          email: responseData.email || profileData.email, // Keep existing email if not in response
          phoneNumber:
            responseData.phone_number ||
            editedData.phoneNumber, // Prefer server value if provided
          // Organization is tenant context — never overwritten by personal profile PUT.
          organizationName: profileData.organizationName,
        };
        setProfileData(updatedProfileData);
        setEditedData(updatedProfileData);
        try {
          if (updatedProfileData.email) {
            sessionStorage.setItem("UserEmail", updatedProfileData.email);
          }
          if (updatedProfileData.phoneNumber) {
            sessionStorage.setItem(
              "sessionPhoneNumber",
              updatedProfileData.phoneNumber,
            );
          }
          // Keep localStorage user in sync so My Facility can prefill on refresh.
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const user = JSON.parse(userStr);
            const updatedUser = {
              ...user,
              first_name: updatedProfileData.firstName,
              last_name: updatedProfileData.lastName,
              phone_number: updatedProfileData.phoneNumber || user.phone_number,
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(
              new CustomEvent("unicomply:user-updated", {
                detail: { user: updatedUser },
              }),
            );
          } else {
            window.dispatchEvent(
              new CustomEvent("unicomply:user-updated", {
                detail: { user: updatedProfileData },
              }),
            );
          }
        } catch (_) {}
        setIsEditMode(false);
        toast.success(response.message || "Profile updated successfully!");
      } else {
        toast.error(response.message || "Failed to update profile. Please try again.");
      }
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      const errorMessage = error.message || "Failed to update profile. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <PageContainer>
      <HeaderContainer
        style={{
          height: PROFILE_TITLE_BAND_HEIGHT_PX,
          minHeight: PROFILE_TITLE_BAND_HEIGHT_PX,
          padding: "0 64px 24px",
          display: "flex",
          alignItems: "flex-end",
          boxSizing: "border-box",
        }}
      >
        <HeaderTitle>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#262626" />
          </svg>
          <Title>Profile</Title>
        </HeaderTitle>
      </HeaderContainer>

      <ContentSection>
        <TabsContainer>
          <Tab isActive={activeTab === "personal"} onClick={() => setActiveTab("personal")}>
            Personal Details
          </Tab>
          <Tab isActive={activeTab === "password"} onClick={() => setActiveTab("password")}>
            Change Password
          </Tab>
        </TabsContainer>

        {activeTab === "personal" && (
          <FormContainer>
            <ButtonContainer>
              {!isEditMode ? (
                <EditButton onClick={handleEdit}>Edit</EditButton>
              ) : (
                <>
                  <SaveButton onClick={handleSave} disabled={loading}>
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
                  </SaveButton>
                  <CancelButton onClick={handleCancel} disabled={loading}>
                    Cancel
                  </CancelButton>
                </>
              )}
            </ButtonContainer>

            <FieldsRow>
              <FieldContainer>
                <FieldLabel>First Name</FieldLabel>
                {!isEditMode ? (
                  <FieldValue>{profileData.firstName || "N/A"}</FieldValue>
                ) : (
                  <StyledTextField value={editedData.firstName} onChange={(e) => handleInputChange("firstName", e.target.value)} placeholder="First Name" fullWidth />
                )}
              </FieldContainer>

              <FieldContainer>
                <FieldLabel>Last Name</FieldLabel>
                {!isEditMode ? (
                  <FieldValue>{profileData.lastName || "N/A"}</FieldValue>
                ) : (
                  <StyledTextField value={editedData.lastName} onChange={(e) => handleInputChange("lastName", e.target.value)} placeholder="Last Name" fullWidth />
                )}
              </FieldContainer>
            </FieldsRow>

            <FieldsRow>
              <FieldContainer>
                <FieldLabel>Email</FieldLabel>
                <FieldValue>{profileData.email || "N/A"}</FieldValue>
              </FieldContainer>

              <FieldContainer>
                <FieldLabel>Phone Number</FieldLabel>
                {!isEditMode ? (
                  <FieldValue>{profileData.phoneNumber || "N/A"}</FieldValue>
                ) : (
                  <StyledTextField value={editedData.phoneNumber} onChange={(e) => handleInputChange("phoneNumber", e.target.value)} placeholder="Phone Number" fullWidth />
                )}
              </FieldContainer>
            </FieldsRow>

            <FieldsRow>
              <FieldContainer>
                <FieldLabel>Organization Name</FieldLabel>
                <FieldValue>{profileData.organizationName || "N/A"}</FieldValue>
              </FieldContainer>
            </FieldsRow>
          </FormContainer>
        )}

        {activeTab === "password" && <ChangePasswordContent />}
      </ContentSection>
    </PageContainer>
  );
};

export default Profile;


